/**
 * ArcGIS Velocity Configuration Types
 *
 * Type definitions for generating Velocity JSON configurations.
 * Based on actual Velocity backup export format.
 */

/**
 * Target platform for migration
 */
export type TargetPlatform = 'velocity-online' | 'velocity-enterprise';

/**
 * Human-readable label for a `TargetPlatform`. Use this in any
 * user-facing surface (HTML migration guide, executive summary,
 * dropdown labels, etc.) — the raw enum values (`velocity-online`,
 * `velocity-enterprise`) are an internal serialization detail and
 * should never be shown directly.
 */
export function formatTargetPlatformLabel(platform: TargetPlatform | string): string {
  switch (platform) {
    case 'velocity-online':
      return 'Velocity for ArcGIS Online';
    case 'velocity-enterprise':
      return 'Velocity for ArcGIS Enterprise';
    default:
      return String(platform);
  }
}

/**
 * Root Velocity backup/export configuration object
 */
export interface VelocityConfiguration {
  orgId: string;
  timestamp: number;
  feeds: string[]; // JSON-encoded VelocityFeedItem strings
  realtime: string[]; // JSON-encoded VelocityRealtimeAnalytic strings
}

/**
 * UI positioning properties
 */
export interface UIProperties {
  width: number;
  height: number;
  top: number;
  left: number;
}

/**
 * Attribute definition in schema
 */
export interface SchemaAttribute {
  name: string;
  dataType: string;
  nullable: boolean;
  tags: string[];
}

/**
 * Geometry definition in schema
 */
export interface SchemaGeometry {
  geometryType: string;
  spatialReference: {
    wkid: number;
    latestWkid?: number;
  };
  hasZ: boolean;
  hasM: boolean;
}

/**
 * Field mapping in schema transformation
 */
export interface FieldMapping {
  fromField: string;
  toField: string;
  tags: string[];
}

/**
 * Schema transformation for feeds
 */
export interface SchemaTransformation {
  inputSchema: {
    attributes: SchemaAttribute[];
    geometry?: SchemaGeometry;
    time?: {
      timeType: string;
    };
  };
  fieldMappings: FieldMapping[];
}

/**
 * Feed configuration
 */
export interface FeedConfig {
  name: string;
  formatName?: string;
  schemaTransformation?: SchemaTransformation;
  properties: Record<string, unknown>;
}

/**
 * Recurrence schedule for feeds
 */
export interface FeedRecurrence {
  expression: string;
  timeZone: string;
  suspend: boolean;
}

/**
 * Item status
 */
export interface ItemStatus {
  startTime?: number;
  lastStartTime?: number;
  stoppedTime?: number;
  executionTime?: number;
  lastKnownAvgByteRate?: number;
  itemState: string;
  lastKnownAvgFeatureRate?: number;
}

/**
 * Velocity feed (input) definition - actual structure
 */
export interface VelocityFeedItem {
  id: string;
  label: string;
  description: string;
  feed: FeedConfig;
  recurrence?: FeedRecurrence;
  properties: {
    executable: boolean;
    folderName: string | null;
  };
  status?: ItemStatus;
  shareProperties: boolean;
}

/**
 * Which part of the Velocity model a flow-diagram node represents.
 */
export type VelocityFlowNodeKind = 'feed' | 'source' | 'tool' | 'output';

/**
 * A single node in a Velocity-styled flow diagram.
 */
export interface VelocityFlowNode {
  id: string;
  label: string;
  kind: VelocityFlowNodeKind;
  /** Velocity component/tool name, used for icon lookup (e.g. `feature-layer`). */
  velocityName: string;
}

/**
 * An edge in a Velocity flow diagram.
 */
export interface VelocityFlowEdge {
  from: string;
  to: string;
}

/**
 * The Velocity-side flow graph for one real-time analytic.
 */
export interface VelocityFlowGraph {
  nodes: VelocityFlowNode[];
  edges: VelocityFlowEdge[];
}

/**
 * A vendored Velocity component icon, split into its viewBox and inner markup
 * so it can be safely embedded as a nested `<svg>` in the generated diagram.
 */
export interface VelocityIconEntry {
  viewBox: string;
  body: string;
}

/**
 * Map of Velocity component name → vendored icon.
 */
export type VelocityIconMap = Record<string, VelocityIconEntry>;

/**
 * Feed reference in realtime analytic
 */
export interface AnalyticFeedRef {
  id: string;
  label: string;
  name: string;
  properties: {
    ui: UIProperties;
    [key: string]: unknown;
  };
}

/**
 * Data source in realtime analytic (e.g., feature-layer for enrichment)
 */
export interface AnalyticSource {
  id: string;
  name: string;
  label: string;
  schemaTransformation?: Record<string, unknown>;
  properties: Record<string, unknown>;
}

/**
 * Tool (processor) in realtime analytic
 */
export interface AnalyticTool {
  id: string;
  name: string;
  label: string;
  properties: Record<string, unknown>;
}

/**
 * Output in realtime analytic
 */
export interface AnalyticOutput {
  id: string;
  name: string;
  label: string;
  formatName?: string;
  properties: Record<string, unknown>;
}

/**
 * Pipeline connection endpoint
 */
export interface PipelineEndpoint {
  id: string;
  port: string;
  properties: Record<string, unknown>;
}

/**
 * Pipeline connection
 */
export interface PipelineConnection {
  from: PipelineEndpoint;
  tos: PipelineEndpoint[];
}

/**
 * Velocity realtime analytic definition - actual structure
 */
export interface VelocityRealtimeAnalytic {
  id: string;
  label: string;
  description: string;
  sources: AnalyticSource[];
  feeds: AnalyticFeedRef[];
  tools: AnalyticTool[];
  outputs: AnalyticOutput[];
  pipeline: PipelineConnection[];
  properties: {
    smartLayoutMode?: string;
    executable: boolean;
    folderName: string | null;
  };
  status?: ItemStatus;
  shareProperties: boolean;
}

// Legacy types for backward compatibility with existing code
export interface VelocityFeed {
  id: string;
  label: string;
  feedType: string;
  properties: Record<string, unknown>;
}

export interface VelocityOutput {
  id: string;
  label: string;
  outputType: string;
  properties: Record<string, unknown>;
}

export interface VelocityAnalytic {
  id: string;
  label: string;
  analyticType: string;
  sources: string[];
  outputs: string[];
  properties: Record<string, unknown>;
}
